import { useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ChefHat, BedDouble, Baby, Box, DoorOpen, Layers, PenTool, LayoutGrid, ArrowRight } from 'lucide-react';
import Section from '../layout/Section';
import siteContent from '../../data/siteContent.json';

const iconMap = {
    kitchen: ChefHat,
    bedroom: BedDouble,
    kids: Baby,
    storage: Box,
    doors: DoorOpen,
    // Keep both to tolerate older JSON keys and avoid rendering undefined
    door: DoorOpen,
    flooring: Layers,
    carving: PenTool,
    shelf: LayoutGrid
};

const ServiceCard = ({ icon: Icon, title, description, index, onClick }) => (
    <motion.div
        initial={{ opacity: 0, y: 30 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true }}
        transition={{ duration: 0.5, delay: index * 0.1 }}
        className="bg-white p-3 sm:p-4 md:p-8 rounded-lg md:rounded-xl shadow-sm hover:shadow-xl transition-all duration-300 border border-transparent hover:border-accent-gold/20 group cursor-pointer focus-within:ring-2 focus-within:ring-accent-gold/50"
    >
        <div className="w-9 h-9 md:w-14 md:h-14 bg-secondary-cream rounded-2xl flex items-center justify-center text-primary-wood mb-3 md:mb-6 group-hover:bg-primary-wood group-hover:text-white transition-colors duration-300">
            <Icon size={20} />
        </div>
        <h3 className="font-heading font-bold text-sm sm:text-base md:text-xl mb-2 md:mb-3 text-primary-charcoal group-hover:text-primary-wood transition-colors">{title}</h3>
        <p className="text-gray-600 mb-3 md:mb-6 leading-relaxed line-clamp-2 md:line-clamp-3 text-[11px] sm:text-sm">
            {description}
        </p>
        <button
            type="button"
            onClick={onClick}
            className="sr-only"
            aria-label={`${title} detaylarını incele`}
        />
    </motion.div>
);

const Services = () => {
    const { services } = siteContent;
    const [activeService, setActiveService] = useState(null);

    useEffect(() => {
        if (!activeService) return;
        const handleKey = (event) => {
            if (event.key === 'Escape') setActiveService(null);
        };
        window.addEventListener('keydown', handleKey);
        document.body.style.overflow = 'hidden';
        return () => {
            window.removeEventListener('keydown', handleKey);
            document.body.style.overflow = 'unset';
        };
    }, [activeService]);

    return (
        <Section id="services" background="cream" className="py-16 md:py-32">
            <div className="text-center max-w-3xl mx-auto mb-10 md:mb-16">
                <span className="text-primary-walnut font-bold tracking-widest uppercase text-sm mb-2 block">
                    {services.badge}
                </span>
                <h2 className="text-2xl md:text-5xl font-heading font-bold text-primary-charcoal mb-3 md:mb-4">
                    {services.title.split('Buluşma')[0]} <br />
                    <span className="text-primary-walnut">Buluşma Noktası</span>
                </h2>
                <p className="text-gray-600 text-base md:text-lg">
                    {services.description}
                </p>
            </div>

            <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 md:gap-6">
                {services.items.map((service, index) => (
                    <ServiceCard
                        key={index}
                        icon={iconMap[service.id] || Box}
                        title={service.title}
                        description={service.desc}
                        index={index}
                        onClick={() => setActiveService(service)}
                    />
                ))}
            </div>

            <AnimatePresence>
                {activeService && (
                    <motion.div
                        className="fixed inset-0 z-[9998] bg-black/70 flex items-center justify-center p-6"
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        onClick={() => setActiveService(null)}
                        role="presentation"
                    >
                        <motion.div
                            className="bg-white max-w-lg w-full rounded-2xl shadow-2xl p-8 relative"
                            initial={{ scale: 0.95, opacity: 0 }}
                            animate={{ scale: 1, opacity: 1 }}
                            exit={{ scale: 0.95, opacity: 0 }}
                            transition={{ duration: 0.2 }}
                            onClick={(event) => event.stopPropagation()}
                            role="dialog"
                            aria-modal="true"
                            aria-label={`${activeService.title} detayları`}
                        >
                            <div className="flex items-start justify-between gap-4">
                                <div>
                                    <p className="text-xs uppercase tracking-widest text-primary-walnut font-semibold mb-2">Hizmet Detayı</p>
                                    <h3 className="text-2xl font-heading font-bold text-primary-charcoal">{activeService.title}</h3>
                                </div>
                                <button
                                    type="button"
                                    onClick={() => setActiveService(null)}
                                    className="text-primary-charcoal/60 hover:text-primary-charcoal text-lg"
                                    aria-label="Detay penceresini kapat"
                                >
                                    ✕
                                </button>
                            </div>
                            <p className="text-gray-600 mt-4 leading-relaxed">
                                {activeService.desc}
                            </p>
                            <div className="mt-6 flex flex-wrap gap-3">
                                <a
                                    href="#contact"
                                    className="px-4 py-2 rounded-full bg-accent-gold text-white text-sm font-semibold hover:bg-[#b08d55] transition-colors"
                                    onClick={() => setActiveService(null)}
                                >
                                    Keşif Planla
                                </a>
                                <button
                                    type="button"
                                    className="px-4 py-2 rounded-full border border-primary-charcoal/20 text-primary-charcoal text-sm font-semibold hover:border-primary-charcoal/60 transition-colors"
                                    onClick={() => setActiveService(null)}
                                >
                                    Kapat
                                </button>
                            </div>
                        </motion.div>
                    </motion.div>
                )}
            </AnimatePresence>
        </Section>
    );
};



export default Services;
